import React, { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react'
import useMessageStore from '../stores/messageStore'

const Contact = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  // Get message store action
  const sendMessage = useMessageStore((state) => state.sendMessage)

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Determine message type based on subject or content
      let messageType = 'General Inquiry'
      const subjectLower = formData.subject.toLowerCase()
      const messageLower = formData.message.toLowerCase()

      if (subjectLower.includes('admission') || messageLower.includes('admission')) {
        messageType = 'Admission Inquiry'
      } else if (subjectLower.includes('fee') || messageLower.includes('fee') || messageLower.includes('payment')) {
        messageType = 'Fee Inquiry'
      } else if (subjectLower.includes('transport') || messageLower.includes('bus') || messageLower.includes('transport')) {
        messageType = 'Transportation'
      } else if (subjectLower.includes('academic') || messageLower.includes('curriculum') || messageLower.includes('study')) {
        messageType = 'Academic Inquiry'
      } else if (subjectLower.includes('event') || messageLower.includes('activity') || messageLower.includes('program')) {
        messageType = 'Events & Activities'
      }

      // Send message via store
      const result = await sendMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        type: messageType
      })

      if (result.success) {
        setIsSubmitting(false)
        setIsSubmitted(true)
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        })

        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false)
        }, 5000)
      } else {
        throw new Error(result.message || 'Failed to send message')
      }

    } catch (error) {
      console.error('Error submitting message:', error)
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: t('contact.address'),
      details: [
        t('contact.addressDetails.complex'),
        t('contact.addressDetails.managedBy'),
        t('contact.addressDetails.postBox'),
        t('contact.addressDetails.country')
      ]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: t('contact.phone'),
      details: [
        t('contact.phoneDetails.office'),
        t('contact.phoneDetails.admissionHelpline')
      ]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: t('contact.email'),
      details: [
        "mandavadsankul1996@gmail.com"
      ]
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: t('contact.hours'),
      details: [
        t('contact.mondayFriday'),
        t('contact.saturday'),
        t('contact.sunday')
      ]
    }
  ]

  return (
    <section id="contact" className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-primary-purple font-poppins mb-4">
              {t('contact.title')}
            </h2>
            <div className="w-24 h-1 bg-primary-yellow mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-primary-purple mb-6 font-montserrat">
                {t('contact.title')}
              </h3>

              {isSubmitted && (
                <div className="mb-6 p-4 bg-primary-green/10 border border-primary-green/20 rounded-lg flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary-green" />
                  <p className="text-primary-green font-medium">
                    {t('contact.successMessage')}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                      {t('contact.fullName')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent transition-all duration-300"
                      placeholder={t('contact.placeholders.enterName')}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                      {t('contact.emailAddress')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent transition-all duration-300"
                      placeholder={t('contact.placeholders.enterEmail')}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                      {t('contact.phoneNumber')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent transition-all duration-300"
                      placeholder={t('contact.placeholders.enterPhone')}
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                      {t('contact.subject')} *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent transition-all duration-300"
                    >
                      <option value="">{t('contact.subjects.selectSubject')}</option>
                      <option value="admission">{t('contact.subjects.admission')}</option>
                      <option value="academic">{t('contact.subjects.academic')}</option>
                      <option value="facilities">{t('contact.subjects.facilities')}</option>
                      <option value="transport">{t('contact.subjects.transport')}</option>
                      <option value="general">{t('contact.subjects.general')}</option>
                      <option value="complaint">{t('contact.subjects.complaint')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent transition-all duration-300 resize-vertical"
                    placeholder={t('contact.placeholders.enterMessage')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-purple text-white py-4 rounded-lg font-semibold hover:bg-primary-magenta disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 hover:scale-105"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{t('contact.sending')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>{t('contact.send')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              {contactInfo.map((info, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-purple/10 rounded-lg flex items-center justify-center text-primary-purple">
                      {info.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-primary-purple mb-2">
                        {info.title}
                      </h4>
                      <div className="space-y-1">
                        {info.details.map((detail, detailIndex) => (
                          <p key={detailIndex} className="text-gray-600">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Map Placeholder - replaced with embedded Google Map */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="h-64">
                  <iframe
                    title="Shri V.D. Patel Madhyamik School Location"
                    src="https://www.google.com/maps?q=V.D.+Patel+Educational+Complex,+Visavadar-Junagadh+Highway,+Mandavad,+Ta.+Visavadar,+Dist.+Junagadh+-+362130&output=embed&z=16"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full"
                  ></iframe>

                </div>
                <div className="p-4">
                  <a
                    href="https://www.google.com/maps/@?api=1&map_action=map&center=21.3,70.7&zoom=1&basemap=satellite
"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-primary-purple text-white py-2 rounded-lg hover:bg-primary-magenta transition-colors duration-300 font-medium block text-center"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Contact Cards */}
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-r from-primary-purple to-primary-magenta rounded-xl p-6 text-white text-center">
              <Phone className="w-8 h-8 mx-auto mb-3" />
              <h4 className="font-semibold mb-2">{t('footer.emergencyContact')}</h4>
              <p className="text-sm opacity-90">{t('footer.available24x7')}</p>
              <p className="font-medium">+91 9687685102</p>
            </div>
            <div className="bg-gradient-to-r from-primary-green to-primary-blue rounded-xl p-6 text-white text-center">
              <Mail className="w-8 h-8 mx-auto mb-3" />
              <h4 className="font-semibold mb-2">{t('footer.quickEmail')}</h4>
              <p className="text-sm opacity-90">{t('footer.fastResponse')}</p>
              <p className="font-medium">mandavadsankul1996@gmail.com</p>
            </div>
            <div className="bg-gradient-to-r from-primary-yellow to-primary-brown rounded-xl p-6 text-white text-center">
              <Clock className="w-8 h-8 mx-auto mb-3" />
              <h4 className="font-semibold mb-2">{t('footer.visitUs')}</h4>
              <p className="text-sm opacity-90">{t('footer.campusTourAvailable')}</p>
              <p className="font-medium">{t('footer.visitingHours')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact